import {
  ChevronsLeft,
  ChevronsRight,
  MessageCircle,
  Users,
} from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import type React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CollapsibleToolPanel } from "@/components/common/CollapsibleToolPanel";
import { NavItem } from "@/components/ui/nav-item";
import { ChatList } from "@/features/chat/components/ChatList";
import { StudyGroupList } from "@/features/my-study-groups/components/StudyGroupList";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ... [NAV_ITEMS unchanged] ...
const NAV_ITEMS = [
  {
    to: "/favorites",
    iconSrc: "/favorite.svg",
    labelKey: "common.favorites",
    pathMatch: "favorites",
  },
  {
    to: "/event-search",
    iconSrc: "/search.svg",
    labelKey: "common.eventSearch",
    pathMatch: "event-search",
  },
  {
    to: "/all-study-groups",
    iconSrc: "/add.svg",
    labelKey: "common.allStudyGroups",
    pathMatch: "all-study-groups",
  },
] as const;

type ToolKey = "study-groups" | "chat";
const COLLAPSED_TOOL_HEIGHT = "40px";
const ACTIVE_TOOL_STORAGE_KEY = "sidebar.activeTool";

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const [activeTool, setActiveTool] = useState<ToolKey>(() => {
    const stored = localStorage.getItem(ACTIVE_TOOL_STORAGE_KEY);
    return stored === "chat" || stored === "study-groups"
      ? stored
      : "study-groups";
  });

  useEffect(() => {
    localStorage.setItem(ACTIVE_TOOL_STORAGE_KEY, activeTool);
  }, [activeTool]);

  const handleToolSelect = (tool: ToolKey) => {
    setActiveTool((prev) => {
      if (isCollapsed) {
        onToggle(); // Auto-expand when selecting a tool from collapsed state
        return tool;
      }
      if (prev !== tool) {
        return tool;
      }
      return tool === "chat" ? "study-groups" : "chat";
    });
  };

  return (
    <aside
      className={`relative flex h-full min-h-0 flex-col border-r-2 border-study-group-selected bg-variable-collection-light-gray py-[26px] transition-all duration-300 ${isCollapsed ? "w-[64px] items-center" : "w-[307px]"}`}
      style={
        {
          "--sidebar-width": isCollapsed ? "64px" : "307px",
          "--sidebar-padding-left": isCollapsed ? "0px" : "20px",
          "--sidebar-padding-right": isCollapsed ? "0px" : "30px",
        } as React.CSSProperties
      }
    >
      {/* Header: Toggle & Logo */}
      <div
        className={`mb-[22px] flex items-center justify-between ${isCollapsed ? "mt-4 flex-col px-0" : "px-4"}`}
      >
        {!isCollapsed && (
          <Link to="/" className="block transition-opacity hover:opacity-80">
            <h1 className="text-xl font-[number:var(--logo-font-weight)] text-variable-collection-black-gray">
              Study Group Lounge
            </h1>
          </Link>
        )}

        <Button
          variant="ghost"
          size="icon"
          className={
            isCollapsed
              ? "size-8 text-gray-400 hover:text-gray-600"
              : "size-8 text-gray-400 hover:text-gray-600"
          }
          onClick={onToggle}
        >
          {isCollapsed ? (
            <ChevronsRight className="size-5" />
          ) : (
            <ChevronsLeft className="size-5" />
          )}
        </Button>
      </div>

      {/* Navigation */}
      <nav
        className={`mb-[22px] w-full min-w-0 space-y-[22px] overflow-x-hidden ${isCollapsed ? "flex flex-col items-center gap-4 space-y-0 px-2" : "pr-[50px]"}`}
      >
        {NAV_ITEMS.map((item) => {
          const isActive = pathname.includes(item.pathMatch);
          if (isCollapsed) {
            return (
              <TooltipProvider key={item.to} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      to={item.to}
                      className={`rounded-md p-2 ${isActive ? "bg-white shadow-sm" : "hover:bg-white/50"}`}
                    >
                      <img src={item.iconSrc} alt="" className="size-[20px]" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    {t(item.labelKey)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          return (
            <NavItem
              key={item.to}
              to={item.to}
              icon={<img src={item.iconSrc} alt="" className="size-[18px]" />}
              label={t(item.labelKey)}
              isActive={isActive}
            />
          );
        })}
      </nav>

      {/* Tools Section */}
      <div
        className={`flex min-h-0 flex-1 flex-col gap-3 ${isCollapsed ? "w-full items-center px-2" : "pl-[20px] pr-[30px]"}`}
      >
        {isCollapsed ? (
          <div className="mt-4 flex w-full flex-col items-center gap-4 border-t border-gray-300 pt-4">
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToolSelect("study-groups")}
                    className={
                      activeTool === "study-groups" ? "bg-white shadow-sm" : ""
                    }
                  >
                    <Users className="size-5 text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {t("common.myStudyGroups")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>

            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleToolSelect("chat")}
                    className={
                      activeTool === "chat" ? "bg-white shadow-sm" : ""
                    }
                  >
                    <MessageCircle className="size-5 text-gray-600" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">{t("chat.chats")}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        ) : (
          <>
            <CollapsibleToolPanel
              isExpanded={activeTool === "study-groups"}
              collapsedHeight={COLLAPSED_TOOL_HEIGHT}
            >
              <StudyGroupList
                containerHeight="100%"
                containerClassName="h-full p-0"
                isExpanded={activeTool === "study-groups"}
                onHeaderToggle={() => handleToolSelect("study-groups")}
              />
            </CollapsibleToolPanel>
            <CollapsibleToolPanel
              isExpanded={activeTool === "chat"}
              collapsedHeight={COLLAPSED_TOOL_HEIGHT}
            >
              <ChatList
                containerHeight="100%"
                containerClassName="h-full p-0"
                showActions={activeTool === "chat"}
                isExpanded={activeTool === "chat"}
                onHeaderToggle={() => handleToolSelect("chat")}
                showTabs={activeTool === "chat"}
              />
            </CollapsibleToolPanel>
          </>
        )}
      </div>
    </aside>
  );
};
